/**
 * Atendente IA — block editor (OpenSpec #2.5 §6).
 *
 * Layout: sidebar (lista de blocos) + editor central + preview MD.
 *
 * Read-only para roles `vendedor`/`atendente`. Save bloqueado quando dirty
 * flag + erro de validação on-save (mensagem inline anexada ao bloco).
 *
 * Não há "Sugerir com IA" funcional nesta change — os botões existem como
 * placeholders desabilitados. Assistente IA chega em OpenSpec posterior.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { SdrBlock, SdrConversationBlocksPack } from '../../types';
import {
  getSdrConversationBlocks,
  putSdrConversationBlocks,
  checkBlockRealtime,
  PutBlocksError,
} from '../../services/iaConsoleSdr';
import { buildAtendenteMarkdown, isAllBlocksEmpty } from './buildAtendenteMarkdown';

interface Props {
  canEdit: boolean;
  /** OpenSpec unify-ia-settings Fase 1 — quando true, renderiza sem
   * cabeçalho fullscreen / sem container externo (componente vive dentro
   * de uma tab maior). Default false (modo fullscreen via rota dedicada). */
  embeddedMode?: boolean;
}

export const AtendenteIaBuilder: React.FC<Props> = ({ canEdit, embeddedMode = false }) => {
  const [pack, setPack] = useState<SdrConversationBlocksPack | null>(null);
  const [blocks, setBlocks] = useState<SdrBlock[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getSdrConversationBlocks()
      .then((res) => {
        if (cancelled) return;
        setPack(res);
        setBlocks(res.blocks);
        setActiveId(res.blocks[0]?.id || null);
        setDirty(false);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Erro ao carregar blocos');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const active = useMemo(() => blocks.find((b) => b.id === activeId) || null, [blocks, activeId]);

  // OpenSpec #2.6 §11 — real-time Layer A validation while typing.
  // Debounced 1.5s; only sub-5ms regex/blocklist check, no LLM call.
  useEffect(() => {
    if (!active || !canEdit) return;
    if (!active.body || active.body.length < 3) return;
    let cancelled = false;
    const activeIdSnapshot = active.id;
    const timer = setTimeout(async () => {
      try {
        const res = await checkBlockRealtime(active.body, `blocks.${activeIdSnapshot}.body`);
        if (cancelled) return;
        if (!res.approved) {
          setFieldErrors((prev) => ({
            ...prev,
            [activeIdSnapshot]: `${res.category || 'bloqueado'}: ${res.reason || 'conteúdo não permitido'}`,
          }));
        }
      } catch {
        // Real-time errors are tolerated — full validation runs on save anyway.
      }
    }, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active?.id, active?.body, canEdit]);

  const updateActive = (patch: Partial<SdrBlock>) => {
    if (!active) return;
    setBlocks((prev) => prev.map((b) => (b.id === active.id ? { ...b, ...patch } : b)));
    setDirty(true);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[active.id];
      return next;
    });
  };

  const addBlock = () => {
    const maxOrder = blocks.reduce((m, b) => Math.max(m, b.order), 0);
    const newBlock: SdrBlock = {
      id: `b-${Date.now()}`,
      title: 'Novo bloco',
      level: 2,
      description: '',
      body: '',
      icon: '✏️',
      order: maxOrder + 1,
    };
    setBlocks((prev) => [...prev, newBlock]);
    setActiveId(newBlock.id);
    setDirty(true);
  };

  const removeBlock = (id: string) => {
    if (!window.confirm('Excluir este bloco? A ação só será aplicada ao salvar.')) return;
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    if (activeId === id) {
      setActiveId(blocks.find((b) => b.id !== id)?.id || null);
    }
    setDirty(true);
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      const next = await putSdrConversationBlocks(blocks);
      setPack(next);
      setBlocks(next.blocks);
      setDirty(false);
    } catch (err) {
      const pbe = err as PutBlocksError;
      const failures = pbe.failures ?? (pbe.field ? [{ field: pbe.field, reason: pbe.reason, category: pbe.category }] : []);
      // Map each failure back to its bloco e mostra label do bloco + category + reason.
      const fieldErrs: Record<string, string> = {};
      let firstBlockId: string | null = null;
      for (const f of failures) {
        const m = /^blocks\[(\d+)\]\.(\w+)/.exec(f.field || '');
        if (!m) continue;
        const idx = Number(m[1]);
        const block = blocks[idx];
        if (!block) continue;
        const cat = f.category ? ` (${f.category})` : '';
        fieldErrs[block.id] = `${f.reason || 'Conteúdo recusado'}${cat}`;
        if (!firstBlockId) firstBlockId = block.id;
      }
      if (Object.keys(fieldErrs).length > 0) setFieldErrors(fieldErrs);
      if (firstBlockId) setActiveId(firstBlockId);
      // Banner principal: lista os blocos afetados + a 1ª razão. Permite o
      // usuário entender QUAL bloco precisa editar sem garimpar o JSON.
      if (failures.length > 0) {
        const blockLabels = failures
          .map((f) => {
            const m = /^blocks\[(\d+)\]/.exec(f.field || '');
            if (!m) return null;
            const idx = Number(m[1]);
            return blocks[idx]?.title || `bloco #${idx + 1}`;
          })
          .filter(Boolean) as string[];
        const head = blockLabels.length > 0
          ? `Não foi possível salvar — ${blockLabels.join(', ')}`
          : 'Não foi possível salvar';
        const first = failures[0];
        const cat = first.category ? ` [${first.category}]` : '';
        setError(`${head}${cat}: ${first.reason || pbe.message || 'conteúdo recusado pelos guardrails'}`);
      } else {
        setError(pbe.message || 'Erro ao salvar');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!pack) {
    return (
      <div style={{ padding: 24, color: error ? '#dc2626' : '#64748b' }}>
        {error || 'Carregando…'}
      </div>
    );
  }

  const preview = buildAtendenteMarkdown(blocks);
  const hasErrors = Object.keys(fieldErrors).length > 0;
  const allEmpty = isAllBlocksEmpty(blocks);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '260px 1fr 340px',
        gap: 16,
        // OpenSpec unify-ia-settings Fase 1: embeddedMode encolhe padding
        // pra encaixar dentro da tab "Atendente IA & SDR" sem dobrar espaço.
        padding: embeddedMode ? '8px 0 16px' : '18px 28px 28px',
        minHeight: embeddedMode ? 500 : 600,
      }}
    >
      {/* Sidebar */}
      <aside
        style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          padding: 14,
          height: 'fit-content',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.5 }}>
            ESTRUTURA DO ATENDENTE
          </div>
          <span
            style={{
              background: '#e0e7ff',
              color: '#4338ca',
              padding: '1px 6px',
              borderRadius: 4,
              fontSize: 11,
            }}
          >
            {blocks.length}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...blocks]
            .sort((a, b) => a.order - b.order)
            .map((s, i) => {
              const isActive = activeId === s.id;
              const hasError = !!fieldErrors[s.id];
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveId(s.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid ' + (isActive ? '#c7d2fe' : 'transparent'),
                    background: hasError ? '#fef2f2' : isActive ? '#eef2ff' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: 14 }}>{s.icon || '•'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: isActive ? 700 : 600,
                        color: hasError ? '#b91c1c' : isActive ? '#3730a3' : '#0f172a',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.title}
                    </div>
                    <div style={{ fontSize: 10.5, color: '#64748b' }}>
                      H{s.level} · {s.body.length} chars
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      color: '#94a3b8',
                      fontFamily: 'JetBrains Mono, monospace',
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </button>
              );
            })}
        </div>
        {canEdit && (
          <button
            onClick={addBlock}
            style={{
              marginTop: 10,
              width: '100%',
              padding: '8px',
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              background: '#fff',
              cursor: 'pointer',
              fontSize: 12.5,
            }}
          >
            + Novo bloco
          </button>
        )}
      </aside>

      {/* Editor */}
      <main
        style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          padding: 24,
          minHeight: 500,
        }}
      >
        {!active ? (
          <div style={{ color: '#64748b', fontSize: 13 }}>Selecione um bloco à esquerda.</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 24 }}>{active.icon || '•'}</span>
              <input
                value={active.title}
                disabled={!canEdit}
                onChange={(e) => updateActive({ title: e.target.value })}
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  border: 0,
                  padding: '4px 0',
                  outline: 'none',
                  flex: 1,
                }}
              />
              <select
                value={active.level}
                disabled={!canEdit}
                onChange={(e) => updateActive({ level: Number(e.target.value) as 1 | 2 | 3 })}
                style={{ padding: '4px 8px', fontSize: 11.5, fontWeight: 600 }}
              >
                <option value={1}>H1</option>
                <option value={2}>H2</option>
                <option value={3}>H3</option>
              </select>
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 18 }}>
              Aparece como{' '}
              <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>
                {'#'.repeat(active.level)} {active.title}
              </code>{' '}
              no prompt final.
            </div>

            <label
              style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4 }}
            >
              Descrição (guia interna — não vai pro prompt)
            </label>
            <textarea
              value={active.description || ''}
              disabled={!canEdit}
              onChange={(e) => updateActive({ description: e.target.value })}
              rows={2}
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                marginBottom: 18,
                fontFamily: 'inherit',
                fontSize: 13,
              }}
            />

            <label
              style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4 }}
            >
              Conteúdo (vai pro prompt)
            </label>
            <textarea
              value={active.body}
              disabled={!canEdit}
              onChange={(e) => updateActive({ body: e.target.value })}
              rows={9}
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid ' + (fieldErrors[active.id] ? '#dc2626' : '#cbd5e1'),
                borderRadius: 6,
                fontFamily: 'inherit',
                fontSize: 13,
              }}
            />
            {fieldErrors[active.id] && (
              <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>
                {fieldErrors[active.id]}
              </div>
            )}

            {canEdit && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button
                  disabled
                  title="Disponível em OpenSpec futura"
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    background: '#f8fafc',
                    color: '#94a3b8',
                    cursor: 'not-allowed',
                  }}
                >
                  ✦ Sugerir com IA
                </button>
                <button
                  disabled
                  title="Disponível em OpenSpec futura"
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    background: '#f8fafc',
                    color: '#94a3b8',
                    cursor: 'not-allowed',
                  }}
                >
                  Reescrever
                </button>
                <div style={{ flex: 1 }} />
                <button
                  onClick={() => removeBlock(active.id)}
                  style={{
                    padding: '6px 12px',
                    border: 0,
                    background: 'transparent',
                    color: '#dc2626',
                    cursor: 'pointer',
                  }}
                >
                  Excluir bloco
                </button>
              </div>
            )}
          </>
        )}

        <div
          style={{
            borderTop: '1px solid #e2e8f0',
            marginTop: 24,
            paddingTop: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, fontSize: 12, color: '#64748b' }}>
            Versão: <strong>{pack.version}</strong>
            {pack.updatedBy ? ` · por ${pack.updatedBy}` : ''}
          </div>
          {canEdit && (
            <>
              {error && (
                <span
                  style={{
                    color: '#b91c1c',
                    fontSize: 12,
                    maxWidth: 480,
                    lineHeight: 1.4,
                  }}
                >
                  {error}
                </span>
              )}
              <button
                disabled={!dirty || saving || hasErrors}
                onClick={handleSave}
                style={{
                  padding: '8px 16px',
                  background:
                    !dirty || saving || hasErrors
                      ? '#cbd5e1'
                      : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#fff',
                  border: 0,
                  borderRadius: 6,
                  cursor: !dirty || saving || hasErrors ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {saving ? 'Salvando…' : 'Salvar & publicar'}
              </button>
            </>
          )}
        </div>
      </main>

      {/* Preview */}
      <aside
        style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          overflow: 'hidden',
          height: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '10px 14px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>
            Preview · atendente.md
          </div>
          <span
            style={{
              background: '#e0e7ff',
              color: '#4338ca',
              padding: '1px 6px',
              borderRadius: 4,
              fontSize: 11,
            }}
          >
            {pack.version}
          </span>
        </div>
        {allEmpty && (
          <div
            style={{
              margin: '10px 14px 0',
              padding: '10px 12px',
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 8,
              fontSize: 11.5,
              color: '#92400e',
              lineHeight: 1.5,
            }}
          >
            <b>Nenhum bloco preenchido.</b> A IA usará o prompt baseline genérico
            do sistema até você preencher pelo menos um bloco aqui.
          </div>
        )}
        <pre
          style={{
            margin: 0,
            padding: 14,
            fontSize: 11.5,
            lineHeight: 1.55,
            fontFamily: 'JetBrains Mono, monospace',
            overflow: 'auto',
            flex: 1,
            whiteSpace: 'pre-wrap',
            color: '#0f172a',
          }}
        >
          {preview || '_(todos os blocos vazios — fallback baseline em runtime)_'}
        </pre>
      </aside>
    </div>
  );
};
